import { useState } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Bar, Dot, Modal, Field, statusBadge, sC, uc } from "./theme";
import { GET, POST, PATCH, DELETE } from "./api";
import { useLang } from "./i18n";

export default function AdminVMs(props) {
  const { vms, users, servers, images, metricsSummary, getServerIp, fetchAll } = props;
  const { t } = useLang();

  const [showCreate, setShowCreate] = useState(false);
  const [editVm, setEditVm] = useState(null);
  const [reclaimVm, setReclaimVm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [initCreate] = useState({ user_id: "", server: "", image: "ubuntu-base", cpu: 8, ram: 32, disk: 200, gpu_ids: "", password: "" });
  const [createForm, setCreateForm] = useState(initCreate);
  const [editForm, setEditForm] = useState({ cpu: 0, ram: 0, gpu_ids: "", new_disk: "" });
  const [keepData, setKeepData] = useState(true);
  const [mountVm, setMountVm] = useState(null);
  const [mounts, setMounts] = useState([]);
  const [mountForm, setMountForm] = useState({ host_path: "", mount_point: "", readonly: false });
  const [infoVm, setInfoVm] = useState(null);
  const [vmKeys, setVmKeys] = useState([]);
  const [vmCollabs, setVmCollabs] = useState([]);
  const [vmServices, setVmServices] = useState([]);
  const [showKey, setShowKey] = useState(null);
  const [svcForm, setSvcForm] = useState({ name: "", vm_port: "", host_port: "", access: "external" });
  const [cloneVm, setCloneVm] = useState(null);
  const [cloneForm, setCloneForm] = useState({ target_server: "", gpu_ids: "", reason: "", expected_end: "" });

  const filtered = vms.filter(v => {
    if (filter !== "all" && v.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return v.id.toLowerCase().includes(q) || v.user_id.toLowerCase().includes(q) || (v.server||"").toLowerCase().includes(q);
    }
    return true;
  });

  const handleCreate = async () => {
    if (!createForm.user_id || !createForm.server || !createForm.password) {
      alert("필수 항목을 모두 입력하세요."); return;
    }
    setBusy(true);
    try {
      await POST("/api/vms", {
        user_id: createForm.user_id, server: createForm.server,
        image: createForm.image, cpu: Number(createForm.cpu),
        ram: Number(createForm.ram), disk: Number(createForm.disk),
        gpu_ids: createForm.gpu_ids, password: createForm.password,
      });
      setShowCreate(false); setCreateForm(initCreate); fetchAll();
    } catch (e) { alert("VM 생성 실패: " + (e.message || e)); }
    setBusy(false);
  };

  const handleStartStop = async (vm, action) => {
    try { await POST(`/api/vms/${vm.id}/${action}`); fetchAll(); }
    catch (e) { alert(`${action} 실패: ` + (e.message || e)); }
  };

  const openEdit = (vm) => {
    setEditVm(vm);
    setEditForm({ cpu: vm.cpu, ram: vm.ram, gpu_ids: vm.gpu_ids || "", new_disk: "" });
  };

  const handleEdit = async () => {
    if (!editVm) return;
    setBusy(true);
    const errs = [];
    const isTimeoutLike = (m) => /504|499|timeout|gateway/i.test(String(m || ""));
    try {
      await PATCH(`/api/vms/${editVm.id}`, {
        cpu: Number(editForm.cpu), ram: Number(editForm.ram),
        gpu_ids: editForm.gpu_ids,
      });
    } catch (e) {
      if (!isTimeoutLike(e.message || e)) {
        errs.push("CPU/RAM/GPU 업데이트 실패: " + (e.message || e));
      }
    }
    if (editForm.new_disk && Number(editForm.new_disk) > editVm.disk) {
      try {
        await POST(`/api/vms/${editVm.id}/resize-disk`, { disk_gb: Number(editForm.new_disk) });
      } catch (e) {
        if (!isTimeoutLike(e.message || e)) {
          errs.push("디스크 리사이즈 실패: " + (e.message || e));
        }
      }
    }
    if (errs.length) alert(errs.join("\n"));
    else alert("작업이 백그라운드로 진행됩니다. 완료까지 1~3분 정도 걸릴 수 있습니다.");
    setEditVm(null); fetchAll();
    setBusy(false);
  };

  const handleReclaim = async () => {
    if (!reclaimVm) return;
    setBusy(true);
    try {
      await POST(`/api/vms/${reclaimVm.id}/reclaim`, { keep_data: keepData });
      setReclaimVm(null); fetchAll();
    } catch (e) { alert("회수 실패: " + (e.message || e)); }
    setBusy(false);
  };

  const handleDelete = async (vm) => {
    if (!window.confirm(`VM "${vm.id}"을 완전히 삭제하시겠습니까?`)) return;
    try { await DELETE(`/api/vms/${vm.id}`); fetchAll(); }
    catch (e) { alert("삭제 실패: " + (e.message || e)); }
  };

  const openInfo = async (vm) => {
    setInfoVm(vm);
    setShowKey(null);
    setSvcForm({ name: "", vm_port: "", host_port: "", access: "external" });
    try {
      const keys = await GET(`/api/users/${vm.user_id}/ssh-keys`);
      setVmKeys(keys);
    } catch(e) { setVmKeys([]); }
    try {
      const collabs = await GET(`/api/vms/${vm.id}/collaborators`);
      setVmCollabs(collabs);
    } catch(e) { setVmCollabs([]); }
    try {
      const svcs = await GET(`/api/vms/${vm.id}/services`);
      setVmServices(svcs);
    } catch(e) { setVmServices([]); }
  };

  const addService = async () => {
    if (!infoVm || !svcForm.name || !svcForm.vm_port || !svcForm.host_port) return;
    setBusy(true);
    try {
      await POST(`/api/vms/${infoVm.id}/services`, {
        name: svcForm.name, vm_port: Number(svcForm.vm_port),
        host_port: Number(svcForm.host_port), access: svcForm.access,
      });
      const svcs = await GET(`/api/vms/${infoVm.id}/services`);
      setVmServices(svcs);
      setSvcForm({ name: "", vm_port: "", host_port: "", access: "external" });
    } catch(e) { alert("포트 추가 실패: " + e.message); }
    setBusy(false);
  };

  const deleteService = async (svcId) => {
    if (!window.confirm("서비스 포트를 삭제하시겠습니까?")) return;
    try {
      await DELETE(`/api/services/${svcId}`);
      const svcs = await GET(`/api/vms/${infoVm.id}/services`);
      setVmServices(svcs);
    } catch(e) { alert("삭제 실패: " + e.message); }
  };

  const openMounts = async (vm) => {
    setMountVm(vm);
    setMountForm({ host_path: "", mount_point: "", readonly: false });
    try {
      const data = await GET(`/api/vms/${vm.id}/mounts`);
      setMounts(data);
    } catch (e) { setMounts([]); }
  };

  const addMount = async () => {
    if (!mountVm || !mountForm.host_path || !mountForm.mount_point) return;
    setBusy(true);
    try {
      await POST(`/api/vms/${mountVm.id}/mounts`, mountForm);
      const data = await GET(`/api/vms/${mountVm.id}/mounts`);
      setMounts(data);
      setMountForm({ host_path: "", mount_point: "", readonly: false });
    } catch (e) { alert("마운트 실패: " + (e.message || e)); }
    setBusy(false);
  };

  const removeMount = async (mountId) => {
    if (!mountVm || !window.confirm("마운트를 해제하시겠습니까?")) return;
    try {
      await DELETE(`/api/vms/${mountVm.id}/mounts/${mountId}`);
      const data = await GET(`/api/vms/${mountVm.id}/mounts`);
      setMounts(data);
    } catch (e) { alert("해제 실패: " + (e.message || e)); }
  };

  const doClone = async () => {
    if (!cloneVm || !cloneForm.target_server) return;
    setBusy(true);
    try {
      const res = await POST(`/api/vms/${cloneVm.id}/clone`, cloneForm);
      alert(`복제 완료: ${res.new_vm_id} (${cloneForm.target_server}, SSH:${res.ssh_port})`);
      setCloneVm(null);
      fetchAll();
    } catch (e) { alert("복제 실패: " + e.message); }
    setBusy(false);
  };

  const fmtDate = (d) => d ? new Date(d+"Z").toLocaleDateString("ko-KR") : "-";

  const serverOpts = (servers||[]).filter(s=>s.online).map(s=>({v:s.id, l:s.label||s.id}));

  return (
    <div>
      <SectionTitle title={t("vm_management")} action={
        <Btn variant="cyan" small onClick={() => setShowCreate(true)}>+ {t("create_vm")}</Btn>
      }/>

      <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        {["all","running","stopped","reclaimed"].map(f => (
          <Btn key={f} small variant={filter===f?"blue":"ghost"} onClick={() => setFilter(f)}>
            {f==="all"?t("all"):f==="running"?t("running"):f==="stopped"?t("stopped"):t("reclaimed")}
            {f!=="all"&&` (${vms.filter(v=>v.status===f).length})`}
          </Btn>
        ))}
        <input placeholder={t("search")} value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background:C.panel2, border:`1px solid ${C.border2}`, color:C.text,
            padding:"5px 10px", borderRadius:6, fontSize:11, width:220, fontFamily:"monospace", outline:"none" }}/>
      </div>

      <Card style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${C.border}`, color:C.dim2, fontSize:9, textTransform:"uppercase", letterSpacing:1 }}>
              {[t("vm_id"),t("users"),t("server"),"IP","SSH","CPU/RAM/Disk","GPU",t("port_range"),t("status"),t("metrics"),t("created_at"),t("actions")].map(h =>
                <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontWeight:600 }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map(v => {
              const m = metricsSummary[v.id];
              return (
                <tr key={v.id} style={{ borderBottom:`1px solid ${C.border}08` }}>
                  <td style={{padding:"8px 10px"}}><span style={{color:C.cyan,fontWeight:700,fontFamily:"monospace"}}>{v.id}</span></td>
                  <td style={{padding:"8px 10px"}}><span style={{color:uc(v.user_id)}}>{v.user_id}</span></td>
                  <td style={{padding:"8px 10px"}}>{v.server}</td>
                  <td style={{padding:"8px 10px"}}><span style={{fontSize:10,color:C.dim2,fontFamily:"monospace"}}>{v.vm_ip||"-"}</span></td>
                  <td style={{padding:"8px 10px"}}><span style={{fontFamily:"monospace"}}>{v.ssh_port}</span></td>
                  <td style={{padding:"8px 10px"}}><span style={{fontFamily:"monospace",fontSize:10}}>{v.cpu}C / {v.ram}G / {v.disk}G</span></td>
                  <td style={{padding:"8px 10px"}}>
                    {v.gpu_ids ? <Badge label={`GPU ${v.gpu_ids}`} color={C.purple}/> : <span style={{color:C.dim2,fontSize:10}}>-</span>}
                  </td>
                  <td style={{padding:"8px 10px"}}><span style={{fontFamily:"monospace",fontSize:10,color:C.dim2}}>{v.port_range_start}-{v.port_range_end}</span></td>
                  <td style={{padding:"8px 10px"}}>{statusBadge(v.vm_status || v.status)}</td>
                  <td style={{padding:"8px 10px"}}>
                    {m ? (
                      <div style={{display:"flex",gap:4,alignItems:"center"}}>
                        <span style={{fontSize:9,color:C.blue}}>C:{m.cpu_pct?.toFixed(0)}%</span>
                        <span style={{fontSize:9,color:C.purple}}>M:{m.mem_total_mb>0?((m.mem_used_mb/m.mem_total_mb)*100).toFixed(0):0}%</span>
                        {m.gpu_util>0&&<span style={{fontSize:9,color:C.green}}>G:{m.gpu_util?.toFixed(0)}%</span>}
                      </div>
                    ) : <span style={{color:C.dim2,fontSize:9}}>-</span>}
                  </td>
                  <td style={{padding:"8px 10px"}}><span style={{color:C.dim2,fontSize:10}}>{fmtDate(v.created_at)}</span></td>
                  <td style={{padding:"8px 10px"}}>
                    <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                      {(v.vm_status==="stopped"||v.status==="stopped")&&<Btn small variant="green" onClick={()=>handleStartStop(v,"start")}>{t("start")}</Btn>}
                      {(v.vm_status==="running"||v.status==="running")&&<Btn small variant="red" onClick={()=>handleStartStop(v,"stop")}>{t("stop")}</Btn>}
                      <Btn small variant="cyan" onClick={()=>openInfo(v)}>{t("info")}</Btn>
                      {v.status!=="reclaimed"&&<>
                        <Btn small variant="default" onClick={()=>openEdit(v)}>{t("edit")}</Btn>
                        <Btn small variant="blue" onClick={()=>openMounts(v)}>{t("mount")}</Btn>
                        <Btn small variant="purple" onClick={()=>{setCloneVm(v);setCloneForm({target_server:"",gpu_ids:"",reason:"",expected_end:""});}}>{t("clone")}</Btn>
                        <Btn small variant="amber" onClick={()=>{setReclaimVm(v);setKeepData(true);}}>{t("reclaim_vm")}</Btn>
                      </>}
                      <Btn small variant="red" onClick={()=>handleDelete(v)}>{t("delete")}</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length===0&&<tr><td colSpan={12} style={{padding:20,textAlign:"center",color:C.dim2}}>{t("no_vm")}</td></tr>}
          </tbody>
        </table>
      </Card>

      {showCreate && (
        <Modal title={t("create_vm")} onClose={()=>setShowCreate(false)}>
          <Field label={t("select_user")} value={createForm.user_id}
            onChange={v=>setCreateForm({...createForm,user_id:v})}
            options={[{v:"",l:"..."},...users.filter(u=>u.active).map(u=>({v:u.id,l:`${u.id} (${u.name})`}))]}/>
          <Field label={t("select_server")} value={createForm.server}
            onChange={v=>setCreateForm({...createForm,server:v})}
            options={[{v:"",l:"..."},...serverOpts]}/>
          <Field label={t("select_image")} value={createForm.image}
            onChange={v=>setCreateForm({...createForm,image:v})}
            options={(images||[]).map(img=>({v:img.id,l:img.name}))}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            <Field label={t("cpu_cores")} value={createForm.cpu} onChange={v=>setCreateForm({...createForm,cpu:v})} type="number"/>
            <Field label={t("ram_gb")} value={createForm.ram} onChange={v=>setCreateForm({...createForm,ram:v})} type="number"/>
            <Field label={t("disk_gb")} value={createForm.disk} onChange={v=>setCreateForm({...createForm,disk:v})} type="number"/>
          </div>
          <Field label={t("gpu_ids")} value={createForm.gpu_ids}
            onChange={v=>setCreateForm({...createForm,gpu_ids:v})} placeholder="0,1"/>
          <Field label={t("password")} value={createForm.password}
            onChange={v=>setCreateForm({...createForm,password:v})} type="password"/>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setShowCreate(false)}>{t("cancel")}</Btn>
            <Btn variant="cyan" disabled={busy} onClick={handleCreate}>{busy?t("creating"):t("create_vm")}</Btn>
          </div>
        </Modal>
      )}

      {editVm && (
        <Modal title={`${t("edit_vm")}: ${editVm.id}`} onClose={()=>setEditVm(null)}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label={t("cpu_cores")} value={editForm.cpu} onChange={v=>setEditForm({...editForm,cpu:v})} type="number"/>
            <Field label={t("ram_gb")} value={editForm.ram} onChange={v=>setEditForm({...editForm,ram:v})} type="number"/>
          </div>
          <Field label={t("gpu_ids")} value={editForm.gpu_ids}
            onChange={v=>setEditForm({...editForm,gpu_ids:v})} placeholder="0,1,2"/>
          <Field label={`${t("disk_gb")} (${editVm.disk}GB → ${editForm.new_disk || editVm.disk}GB)`}
            value={editForm.new_disk}
            onChange={v=>setEditForm({...editForm,new_disk:v})} type="number"
            placeholder={`${editVm.disk} (현재)`}/>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setEditVm(null)}>{t("cancel")}</Btn>
            <Btn variant="cyan" disabled={busy} onClick={handleEdit}>{busy?t("saving"):t("save")}</Btn>
          </div>
        </Modal>
      )}

      {reclaimVm && (
        <Modal title={`${t("reclaim_vm")}: ${reclaimVm.id}`} onClose={()=>setReclaimVm(null)}>
          <div style={{fontSize:12,color:C.text,marginBottom:14}}>
            VM <span style={{color:C.cyan,fontWeight:700}}>{reclaimVm.id}</span> ({reclaimVm.user_id}){t("confirm_reclaim")}
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,color:C.text,fontSize:12,cursor:"pointer"}}>
            <input type="checkbox" checked={keepData} onChange={e=>setKeepData(e.target.checked)}/>
            {t("keep_data")}
          </label>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setReclaimVm(null)}>{t("cancel")}</Btn>
            <Btn variant="amber" disabled={busy} onClick={handleReclaim}>{busy?t("reclaiming"):t("reclaim_vm")}</Btn>
          </div>
        </Modal>
      )}

      {mountVm && (
        <Modal title={`${t("disk_mount")}: ${mountVm.id}`} onClose={()=>setMountVm(null)} width={600}>
          <div style={{fontSize:11,color:C.dim2,marginBottom:14}}>
            {t("mount_desc")}
          </div>

          {/* Existing mounts */}
          {mounts.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>{t("current_mounts")}</div>
              {mounts.map(m => (
                <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                  padding:"8px 10px",background:C.bg,borderRadius:6,marginBottom:4,border:`1px solid ${C.border}`}}>
                  <div style={{fontFamily:"monospace",fontSize:11}}>
                    <span style={{color:C.cyan}}>{m.host_path}</span>
                    <span style={{color:C.dim2}}> → </span>
                    <span style={{color:C.green}}>{m.mount_point}</span>
                    {m.readonly ? <Badge label="RO" color={C.amber}/> : null}
                  </div>
                  <Btn small variant="red" onClick={()=>removeMount(m.id)}>{t("unmount")}</Btn>
                </div>
              ))}
            </div>
          )}

          {/* Add new mount */}
          <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>{t("new_mount")}</div>
          <Field label={t("host_path")} value={mountForm.host_path}
            onChange={v=>setMountForm({...mountForm,host_path:v})}
            placeholder="/home/username"/>
          <Field label={t("mount_point")} value={mountForm.mount_point}
            onChange={v=>setMountForm({...mountForm,mount_point:v})}
            placeholder="/mnt/data"/>
          <label style={{display:"flex",alignItems:"center",gap:8,color:C.text,fontSize:11,cursor:"pointer",marginBottom:14}}>
            <input type="checkbox" checked={mountForm.readonly}
              onChange={e=>setMountForm({...mountForm,readonly:e.target.checked})}/>
            {t("readonly")}
          </label>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setMountVm(null)}>{t("close")}</Btn>
            <Btn variant="cyan" disabled={busy||!mountForm.host_path||!mountForm.mount_point}
              onClick={addMount}>{busy?t("mounting"):t("mount")}</Btn>
          </div>
        </Modal>
      )}

      {cloneVm && (
        <Modal title={`${t("clone_vm")}: ${cloneVm.id}`} onClose={()=>setCloneVm(null)}>
          <div style={{fontSize:11,color:C.dim2,marginBottom:14,fontFamily:"monospace"}}>
            {cloneVm.id} ({cloneVm.user_id}) → {t("clone_desc")}
          </div>
          <Field label={t("target_server")} value={cloneForm.target_server}
            onChange={v=>setCloneForm({...cloneForm,target_server:v})}
            options={[{v:"",l:"..."},...serverOpts]}/>
          <Field label="GPU" value={cloneForm.gpu_ids}
            onChange={v=>setCloneForm({...cloneForm,gpu_ids:v})}
            placeholder="0,1"/>
          <Field label={t("reason")} value={cloneForm.reason}
            onChange={v=>setCloneForm({...cloneForm,reason:v})}
            placeholder=""/>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
            <Btn variant="ghost" onClick={()=>setCloneVm(null)}>{t("cancel")}</Btn>
            <Btn variant="purple" disabled={busy||!cloneForm.target_server}
              onClick={doClone}>{busy?t("cloning"):t("clone")}</Btn>
          </div>
        </Modal>
      )}

      {infoVm && (
        <Modal title={`${t("connect_info")}: ${infoVm.id}`} onClose={()=>setInfoVm(null)} width={650}>
          {(() => {
            const ip = getServerIp(infoVm.server);
            const username = infoVm.id.replace(/-\d+$/, "");
            const keyCmd = `ssh -i ${infoVm.id}.pem -p ${infoVm.ssh_port} ${username}@${ip}`;
            const rootKeyCmd = `ssh -i ${infoVm.id}.pem -p ${infoVm.ssh_port} root@${ip}`;
            const scpCmd = `scp -i ${infoVm.id}.pem -P ${infoVm.ssh_port} file.txt ${username}@${ip}:~/`;

            const downloadKey = async () => {
              try {
                const token = localStorage.getItem("gpu_fabric_token");
                const res = await fetch(`/api/vms/${infoVm.id}/key`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) { alert("키 다운로드 실패"); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `${infoVm.id}.pem`; a.click();
                URL.revokeObjectURL(url);
              } catch(e) { alert(e.message); }
            };

            const viewKey = async () => {
              if (showKey) { setShowKey(null); return; }
              try {
                const token = localStorage.getItem("gpu_fabric_token");
                const res = await fetch(`/api/vms/${infoVm.id}/key`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) { alert("키 조회 실패"); return; }
                setShowKey(await res.text());
              } catch(e) { alert(e.message); }
            };

            return (<>
              {/* SSH Key */}
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>{t("ssh_key_vm")}</div>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <Btn small variant="cyan" onClick={downloadKey}>{t("key_download")} ({infoVm.id}.pem)</Btn>
                <Btn small variant="default" onClick={viewKey}>{showKey?t("hide"):t("view")}</Btn>
                {showKey && <Btn small variant="ghost" onClick={()=>{navigator.clipboard?.writeText(showKey)}}>{t("copy")}</Btn>}
              </div>
              {showKey && (
                <pre style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,
                  padding:10,fontSize:10,color:C.cyan,fontFamily:"monospace",
                  whiteSpace:"pre-wrap",wordBreak:"break-all",maxHeight:200,overflow:"auto",
                  marginBottom:14}}>{showKey}</pre>
              )}

              {/* SSH 접속 */}
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>{t("ssh_access_key")}</div>
              {[
                {l:t("user_access_key"), v:keyCmd},
                {l:t("root_access_key"), v:rootKeyCmd},
                {l:t("scp_copy"), v:scpCmd},
              ].map(item => (
                <div key={item.l} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,
                  padding:"8px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:8,color:C.dim2,marginBottom:2}}>{item.l}</div>
                    <span style={{fontSize:11,color:C.cyan,fontFamily:"monospace"}}>{item.v}</span>
                  </div>
                  <button onClick={()=>{navigator.clipboard?.writeText(item.v)}}
                    style={{background:"none",border:"none",color:C.dim2,cursor:"pointer",fontSize:10,fontFamily:"monospace"}}>
                    {t("copy")}
                  </button>
                </div>
              ))}


              {/* VM 정보 */}
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginTop:16,marginBottom:8}}>{t("vm_detail")}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:11,fontFamily:"monospace",
                background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:12,marginBottom:14}}>
                {[
                  {l:t("vm_id"), v:infoVm.id},
                  {l:t("owner"), v:`${infoVm.user_id} (${infoVm.user_name})`},
                  {l:t("server"), v:`${infoVm.server} (${ip})`},
                  {l:"VM IP", v:infoVm.vm_ip||"-"},
                  {l:t("ssh_port"), v:infoVm.ssh_port},
                  {l:t("port_range"), v:`${infoVm.port_range_start}-${infoVm.port_range_end}`},
                  {l:"CPU", v:`${infoVm.cpu}`},
                  {l:"RAM", v:`${infoVm.ram} GB`},
                  {l:"Disk", v:`${infoVm.disk} GB`},
                  {l:"GPU", v:infoVm.gpu_ids||t("none")},
                  {l:t("status"), v:infoVm.vm_status||infoVm.status},
                  {l:t("created_at"), v:infoVm.created_at},
                ].map(r => (
                  <div key={r.l}>
                    <span style={{color:C.dim2}}>{r.l}: </span>
                    <span style={{color:C.text}}>{r.v}</span>
                  </div>
                ))}
              </div>

              {/* 서비스 포트 */}
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>
                {t("service_ports")} ({t("range")}: {infoVm.port_range_start}-{infoVm.port_range_end})
              </div>
              {vmServices.length > 0 && (
                <div style={{marginBottom:10}}>
                  {vmServices.map(s => (
                    <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"6px 10px",background:C.bg,borderRadius:6,marginBottom:4,border:`1px solid ${C.border}`}}>
                      <div style={{fontFamily:"monospace",fontSize:11}}>
                        <span style={{color:C.text,fontWeight:700}}>{s.name}</span>
                        <span style={{color:C.dim2}}> — </span>
                        <span style={{color:C.cyan}}>:{s.host_port}</span>
                        <span style={{color:C.dim2}}> → VM:{s.vm_port}</span>
                        <span style={{color:C.dim2}}> ({s.access})</span>
                      </div>
                      <Btn small variant="red" onClick={()=>deleteService(s.id)}>{t("delete")}</Btn>
                    </div>
                  ))}
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr auto",gap:6,alignItems:"end",marginBottom:14}}>
                <Field label={t("service_name")} value={svcForm.name}
                  onChange={v=>setSvcForm({...svcForm,name:v})} placeholder="jupyter"/>
                <Field label={t("vm_port")} value={svcForm.vm_port}
                  onChange={v=>setSvcForm({...svcForm,vm_port:v})} type="number" placeholder="8888"/>
                <Field label={t("host_port")} value={svcForm.host_port}
                  onChange={v=>setSvcForm({...svcForm,host_port:v})} type="number"
                  placeholder={String(infoVm.port_range_start)}/>
                <Field label={t("access")} value={svcForm.access}
                  onChange={v=>setSvcForm({...svcForm,access:v})}
                  options={[{v:"external",l:t("external")},{v:"tunnel",l:t("tunnel")}]}/>
                <Btn small variant="cyan" disabled={busy||!svcForm.name||!svcForm.vm_port||!svcForm.host_port}
                  onClick={addService}>{t("add")}</Btn>
              </div>

              {/* 협업자 */}
              {vmCollabs.length > 0 && (
                <>
                  <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>{t("collaborators")}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                    {vmCollabs.map(c => (
                      <Badge key={c.user_id} label={`${c.user_id} (${c.user_name})`} color={C.blue}/>
                    ))}
                  </div>
                </>
              )}

              {/* SSH 키 */}
              <div style={{fontSize:9,color:C.dim2,letterSpacing:1,marginBottom:8}}>
                {infoVm.user_id}{t("registered_ssh_keys")} ({vmKeys.length})
              </div>
              {vmKeys.length === 0 ? (
                <div style={{fontSize:11,color:C.dim2,padding:10}}>{t("no_ssh_keys")}</div>
              ) : (
                vmKeys.map(k => (
                  <div key={k.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,
                    padding:"8px 12px",marginBottom:4}}>
                    <div style={{fontSize:9,color:C.dim2,marginBottom:2}}>{k.name} ({k.added_at})</div>
                    <div style={{fontSize:10,color:C.text,fontFamily:"monospace",wordBreak:"break-all",
                      maxHeight:40,overflow:"hidden",textOverflow:"ellipsis"}}>{k.key}</div>
                  </div>
                ))
              )}

              <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <Btn variant="ghost" onClick={()=>setInfoVm(null)}>{t("close")}</Btn>
              </div>
            </>);
          })()}
        </Modal>
      )}
    </div>
  );
}
